import { SEOHead } from "@/components/SEOHead";
import { Layout } from "@/components/layout/Layout";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  CheckCircle,
  Target,
  Brain,
  Zap,
  TrendingUp,
  Database,
  Mail,
  Phone,
  Calendar,
  BarChart3,
  ShieldCheck,
  Compass,
  Eye,
  PlayCircle,
} from "lucide-react";

const HowToUseAIInSales = () => {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "How to Use AI in Sales: The Operator's Guide for Revenue Leaders",
      description:
        "A practical guide to applying AI in sales: prospecting, lead scoring, forecasting, B2B execution, and the difference between copilots and autonomous CRM.",
      author: { "@type": "Organization", name: "NoQ5" },
      publisher: {
        "@type": "Organization",
        name: "NoQ5",
        url: "https://noq5.ai",
      },
      datePublished: "2026-05-17",
      dateModified: "2026-05-17",
      mainEntityOfPage: "https://noq5.ai/how-to-use-ai-in-sales",
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "What is AI in sales?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "AI in sales is the use of machine learning and large language models to execute and optimize revenue work: capturing activity from email, calls and meetings; enriching contacts and accounts; scoring leads; forecasting pipeline; and surfacing the next best action. The mature form is autonomous CRM, where an AI agent runs the workflow end to end rather than suggesting work for a rep to do manually.",
          },
        },
        {
          "@type": "Question",
          name: "How do you use AI in sales?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Start by removing manual CRM updates: connect AI to email, calendar and call recordings so every interaction is captured automatically. Then layer AI on top of the captured data for enrichment, lead scoring, forecasting and pipeline risk alerts. The fastest ROI comes from eliminating admin work first, not from generating more outbound.",
          },
        },
        {
          "@type": "Question",
          name: "How can AI help me prospect in sales?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "AI helps prospecting in three concrete ways: enriching cold accounts with firmographic, technographic and intent data; scoring inbound and outbound leads against your closed-won pattern; and surfacing accounts that match a known buying signal (funding, hiring, tech stack change). Generative outbound at scale is a trap. Targeting precision is the real lever.",
          },
        },
        {
          "@type": "Question",
          name: "How does AI improve lead scoring in sales?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Traditional lead scoring uses static point rules (visited pricing page = +10). AI scoring learns from your actual closed-won deals and weights engagement, firmographic fit and behavioral patterns dynamically. It updates in real time as new signals arrive and degrades scores when leads go cold, which static rules cannot do.",
          },
        },
        {
          "@type": "Question",
          name: "How does AI sales forecasting reduce bias?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Rep-reported forecasts are biased by quota anxiety, sandbagging and recency. AI forecasting builds the call from objective signals: meeting cadence, multi-thread coverage, response latency, stage age and historical win rates for similar deal shapes. The CRO ends up with two numbers, the rep call and the AI call, and a clear gap to investigate.",
          },
        },
        {
          "@type": "Question",
          name: "How is AI used in B2B sales specifically?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "B2B is where autonomous CRM matters most because deal cycles are long, multi-threaded and stage-heavy. AI is used to keep account intelligence current across changing buying committees, to detect stalled deals before they slip, to enforce MEDDIC or BANT qualification consistently, and to give RevOps a real source of truth that does not depend on reps remembering to log activity.",
          },
        },
        {
          "@type": "Question",
          name: "What is the difference between an AI copilot and an autonomous CRM?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "A copilot suggests work for a human to do. An autonomous CRM does the work and reports on it. Copilots still require the rep to be the bottleneck. Autonomous CRM removes the bottleneck. For revenue leaders, the practical difference is whether you are buying productivity advice or buying execution.",
          },
        },
      ],
    },
  ];

  return (
    <Layout>
      <SEOHead
        title="How to Use AI in Sales: The Operator's Guide for Revenue Leaders | NoQ5"
        description="A practical guide to using AI in sales: prospecting, lead scoring, forecasting, B2B execution, and why autonomous CRM beats copilots. Written for CROs, not analysts."
        keywords="how to use ai in sales, ai in sales, ai for sales, how can ai be used in sales, how to leverage ai in sales, how ai improves lead scoring in sales, how ai sales forecasting reduces bias, how to use ai in b2b sales, how can ai help me prospect in sales, ai sales assistants, conversational ai in sales, what is ai in sales, autonomous CRM, ai chief of staff for revenue"
        canonicalUrl="https://noq5.ai/how-to-use-ai-in-sales"
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-background via-background to-primary/5">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center">
            <span className="inline-block px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-semibold mb-6">
              THE OPERATOR'S GUIDE
            </span>
            <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6 leading-tight">
              How to Use AI in Sales.<br />
              <span className="text-primary">Without the Copilot Theater.</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-4">
              Most "AI in sales" advice is productivity hacks for reps. This guide is written for the people who own the number. We cover what AI actually changes in prospecting, lead scoring, forecasting and B2B execution, and where the category is heading next.
            </p>
            <p className="text-base text-muted-foreground max-w-3xl mx-auto mb-8">
              Short version: stop buying copilots. Deploy an autonomous CRM and reclaim the 5 to 8 hours per week your reps burn on admin.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground" asChild>
                <Link to="/trial?scrollTo=demo">Book a Demo <ArrowRight className="ml-2 h-5 w-5" /></Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/crm-agent">See the CRM Agent</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Definition */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold mb-6">What Is AI in Sales?</h2>
          <p className="text-lg text-muted-foreground mb-4">
            <strong className="text-foreground">AI in sales</strong> is the use of machine learning and large language models to execute revenue work: capturing activity from email, calls and meetings; enriching contacts and accounts; scoring leads against your real closed-won pattern; forecasting pipeline from objective signals; and surfacing the next best action on every deal.
          </p>
          <p className="text-lg text-muted-foreground mb-4">
            The first wave was copilots. Tools that summarize calls, draft emails, suggest follow-ups. They make a productive rep slightly more productive. They do not move the number.
          </p>
          <p className="text-lg text-muted-foreground">
            The mature form is <strong className="text-foreground">autonomous CRM</strong>. An AI agent that runs the workflow end to end and treats the rep as a data source, not the bottleneck. That is the shift this guide is about.
          </p>
        </div>
      </section>

      {/* The 5 places AI actually moves the number */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-center mb-4">The 5 Places AI Actually Moves the Number</h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
            Ignore the demo videos. These are the only five surfaces where AI produces measurable revenue impact in a B2B sales motion.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                icon: Database,
                title: "1. Activity Capture",
                desc: "Email, calls, meetings auto-logged to the right contact and deal. This alone returns 5 to 8 hours per rep per week. Everything else compounds on top of clean activity data.",
              },
              {
                icon: Target,
                title: "2. Lead Scoring and Routing",
                desc: "Dynamic scoring trained on your closed-won deals, not on static rules. Routes the right lead to the right rep in seconds, scores decay when leads go cold.",
              },
              {
                icon: TrendingUp,
                title: "3. Pipeline Health and Risk",
                desc: "Stalled deals, single-threaded accounts and ghost forecasts surface before QBR. The CRO sees what is real, not what reps want to be real.",
              },
              {
                icon: BarChart3,
                title: "4. Evidence-Based Forecasting",
                desc: "Two forecasts side by side: the rep call and the AI call. The gap is the conversation. Sandbagging and happy ears both become visible.",
              },
              {
                icon: Brain,
                title: "5. Account Intelligence",
                desc: "Buying committee changes, funding events, tech stack shifts, hiring signals. AI keeps the account picture current so reps walk into every meeting prepared.",
              },
              {
                icon: Zap,
                title: "Bonus: Prospecting Precision",
                desc: "Not generative outbound at scale, that is spam. AI finds the 50 accounts most likely to buy this quarter based on real signals, so your AEs work the right list.",
              },
            ].map((item, i) => (
              <div key={i} className="flex gap-4 p-6 bg-card rounded-xl border border-border">
                <item.icon className="h-8 w-8 text-primary flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-lg mb-1">{item.title}</h3>
                  <p className="text-muted-foreground">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Copilot vs Autonomous */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-center mb-4">Copilot vs Autonomous CRM</h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
            The single most important question when you evaluate AI in sales: is this tool advising my reps, or is it doing the work?
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-card p-8 rounded-xl border border-border">
              <h3 className="text-xl font-bold mb-4">AI Copilot</h3>
              <ul className="space-y-3 text-muted-foreground">
                <li>Suggests email drafts, summaries, next steps</li>
                <li>Requires rep to accept, edit, copy, paste</li>
                <li>Productivity gain depends on rep adoption</li>
                <li>Rep is still the bottleneck</li>
                <li>You buy advice</li>
              </ul>
            </div>
            <div className="bg-card p-8 rounded-xl border-2 border-primary/40">
              <h3 className="text-xl font-bold text-primary mb-4">Autonomous CRM</h3>
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex gap-2"><CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" /> Executes CRM updates, enrichment, scoring directly</li>
                <li className="flex gap-2"><CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" /> Runs whether or not the rep logs in</li>
                <li className="flex gap-2"><CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" /> Impact independent of rep behavior</li>
                <li className="flex gap-2"><CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" /> Removes the bottleneck</li>
                <li className="flex gap-2"><CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" /> You buy execution</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* The NoQ5 Approach */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-center mb-4">How NoQ5 Applies AI to Sales</h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
            Capture, Understand, Act. Three layers, one autonomous loop.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Eye,
                title: "1. Capture",
                desc: "Email, calendar, calls and meetings stream into NoQ5 in real time. Zero rep input required.",
              },
              {
                icon: Compass,
                title: "2. Understand",
                desc: "The AI builds an evidence layer on top: account graph, buying committee, deal momentum, risk signals. Always current.",
              },
              {
                icon: PlayCircle,
                title: "3. Act",
                desc: "CRM updated, leads scored, alerts fired, forecasts adjusted, briefings written. The work gets done without a rep clicking anything.",
              },
            ].map((item, i) => (
              <div key={i} className="bg-card p-6 rounded-xl border border-border">
                <item.icon className="h-10 w-10 text-primary mb-4" />
                <h3 className="font-bold text-lg mb-2">{item.title}</h3>
                <p className="text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* First 30 Days */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-center mb-12">What the First 30 Days Look Like</h2>
          <div className="space-y-6">
            {[
              { day: "Day 1 to 3", title: "Connect and listen", desc: "NoQ5 connects to Salesforce, HubSpot, Pipedrive or Zoho, plus Google Workspace or Microsoft 365. Activity capture starts immediately. No workflow rebuilding." },
              { day: "Day 4 to 10", title: "Backfill and baseline", desc: "Historical activity is reconciled. Account intelligence and deal momentum baselines are built from your real pipeline. RevOps sees the first honest forecast." },
              { day: "Day 11 to 20", title: "Autonomous execution", desc: "CRM updates, enrichment, lead scoring and pipeline alerts run autonomously. Reps stop logging activity. Managers stop chasing updates." },
              { day: "Day 21 to 30", title: "Measurable lift", desc: "5 to 8 hours per rep per week recovered. Forecast accuracy improves by 15 to 25 points. Stalled deals identified an average of 11 days earlier." },
            ].map((item, i) => (
              <div key={i} className="flex gap-6 p-6 bg-card rounded-xl border border-border">
                <div className="flex-shrink-0">
                  <div className="text-sm font-semibold text-primary mb-1">{item.day}</div>
                </div>
                <div>
                  <h3 className="font-bold text-lg mb-1">{item.title}</h3>
                  <p className="text-muted-foreground">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-center mb-12">Go Deeper</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: "AI CRM Automation", link: "/ai-crm-automation", desc: "Why rule-based CRM automation fails and what autonomous CRM does differently." },
              { title: "Pipeline Management AI", link: "/pipeline-management-ai", desc: "Stalled deal detection, multi-thread coverage, evidence-based stage advancement." },
              { title: "Lead Qualification AI", link: "/lead-qualification-ai", desc: "Dynamic MEDDIC and BANT scoring trained on your closed-won pattern." },
              { title: "CRM Enrichment Agent", link: "/crm-enrichment-agent", desc: "Account intelligence that stays current. Firmographic, technographic, intent." },
              { title: "AI for CROs", link: "/ai-for-cros", desc: "What revenue leaders actually buy when they buy AI." },
              { title: "Best AI Sales Assistants 2026", link: "/best-ai-sales-assistants-2026", desc: "The honest comparison across the category." },
            ].map((item, i) => (
              <Link key={i} to={item.link} className="bg-card p-6 rounded-xl border border-border hover:border-primary/50 transition-colors group">
                <h3 className="font-bold text-lg mb-2 group-hover:text-primary transition-colors">{item.title}</h3>
                <p className="text-muted-foreground text-sm mb-3">{item.desc}</p>
                <span className="text-primary text-sm font-medium flex items-center gap-1">Read <ArrowRight className="h-4 w-4" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Stop Reading About AI in Sales. See It Run.</h2>
          <p className="text-xl opacity-90 mb-8">
            A 30 minute working session with the founder. Bring your pipeline, leave with a baseline of what autonomous CRM does to your number.
          </p>
          <Button size="lg" variant="secondary" asChild>
            <Link to="/trial?scrollTo=demo">Book a Demo <ArrowRight className="ml-2 h-5 w-5" /></Link>
          </Button>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-3xl font-bold text-center mb-12">AI in Sales: FAQ</h2>
          <div className="space-y-6">
            {[
              { q: "What is AI in sales?", a: "AI in sales is the use of machine learning and LLMs to execute revenue work: capturing activity, enriching accounts, scoring leads, forecasting pipeline and surfacing next best actions. The mature form is autonomous CRM, where the agent runs the workflow end to end." },
              { q: "How do you use AI in sales?", a: "Eliminate manual CRM admin first. Connect AI to email, calendar and call recordings so every interaction is captured automatically. Then layer enrichment, scoring and forecasting on top of clean data. Admin elimination delivers ROI in weeks, not quarters." },
              { q: "How can AI help me prospect in sales?", a: "Three concrete ways: enriching cold accounts with firmographic and intent data, scoring leads against your closed-won pattern, and surfacing accounts matching a known buying signal. Skip generative outbound at scale, precision beats volume." },
              { q: "How does AI improve lead scoring?", a: "AI scoring learns from your actual closed-won deals and weights signals dynamically. It updates in real time and degrades scores when leads go cold. Static rule-based scoring cannot do either." },
              { q: "How does AI sales forecasting reduce bias?", a: "AI forecasts use objective signals: meeting cadence, multi-thread coverage, response latency, stage age, win rates on similar deal shapes. You get two numbers, the rep call and the AI call. The gap is the coaching conversation." },
              { q: "How is AI used in B2B sales?", a: "B2B is where autonomous CRM matters most: long cycles, multi-threaded committees, stage-heavy pipelines. AI keeps account intelligence current, detects stalled deals early, enforces qualification consistently, and gives RevOps a real source of truth independent of rep logging behavior." },
              { q: "What is the difference between an AI copilot and an autonomous CRM?", a: "A copilot suggests work for a human. An autonomous CRM does the work. Copilots leave the rep as the bottleneck. Autonomous CRM removes the bottleneck. One sells advice, the other sells execution." },
              { q: "Does AI in sales replace sales reps?", a: "No. It replaces the parts of the rep's day that are not selling: data entry, CRM hygiene, account research, list building, follow-up tracking. The selling, the negotiation, the relationship work, stays human and becomes a larger share of the rep's time." },
            ].map((item, i) => (
              <div key={i} className="bg-card p-6 rounded-xl border border-border">
                <h3 className="font-bold text-lg mb-2">{item.q}</h3>
                <p className="text-muted-foreground">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default HowToUseAIInSales;
